import React, { ForwardRefExoticComponent, RefAttributes, useContext, useEffect, useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { makeStyles, Theme, createStyles, TextFieldProps } from "@material-ui/core";
import { AxiosResponse } from "axios";
import { useHistory, useParams } from "react-router-dom";
import CustomButton from "../../../components/Button/CustomButton";
import ProductApi from "../../../api/services/ProductApi";
import { TesaPrototype } from "../../../api/models/product/TesaPrototype";
import CustomSearchAutocomplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import PagedResponse from "../../../api/models/PagedResponse";
import useEnv from "../../../hooks/env";
import PrototypeSampleAddFormType from "../../../api/models/product/PrototypeSampleAddFormType";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { TestRequestTabType } from "../useTestRequestParams";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alignRight: {
      textAlign: "right",
    },
    marginTop: {
      marginTop: theme.spacing(2),
    },
  }),
);

const PrototypeSearchAutocomplete = CustomSearchAutocomplete as ForwardRefExoticComponent<
  MergedProps<CustomSearchAutocompleteProps<TesaPrototype>, TextFieldProps> & RefAttributes<{ reset: () => void }>
>;

interface PrototypeSampleAddFormProps {
  halfWidth?: boolean;
  hideSubmit?: boolean;
  handleFormChange?: (values: PrototypeSampleAddFormType | null) => void;
  handleSubmit?: (values: PrototypeSampleAddFormType) => void;
}

const PrototypeSampleAddForm = (props: PrototypeSampleAddFormProps) => {
  const { halfWidth, handleFormChange, handleSubmit, hideSubmit } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const prototypeSearch = useRef<HTMLDivElement & { reset: () => void }>(null);
  const history = useHistory();
  const { testRequestId, tabName } = useParams<{ testRequestId: string; tabName: string }>();
  const classes = useStyles();
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const yupValidationSchema = Yup.object().shape({
    prototype: Yup.mixed().required("Please select"),
  });

  const handleNavigation = () => {
    localStorage.setItem("recentReqId", testRequestId);
    localStorage.setItem("recentTab", tabName);
    localStorage.setItem(
      "recentDialog",
      tabName === TestRequestTabType.PRODUCT.toString()
        ? "addProductPrototype"
        : "addSubstrateSample?type=TESA_PROTOTYPE",
    );

    history.push("/products/prototype/createPrototype");
  };

  return (
    <Formik<PrototypeSampleAddFormType>
      initialValues={{
        prototype: null,
      }}
      onSubmit={(values) => {
        if (handleSubmit) {
          handleSubmit(values);
        }
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<PrototypeSampleAddFormType>): JSX.Element => {
        const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;

        useEffect(() => {
          if (handleFormChange) {
            handleFormChange(values);
          }
        }, [values]);

        return (
          <Form>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={halfWidth ? 6 : 12}>
                <PrototypeSearchAutocomplete
                  autoFocus
                  ref={prototypeSearch}
                  inputLabel="Prototype Name"
                  optionFetch={(query: string): Promise<PagedResponse<TesaPrototype>> => {
                    return productApi
                      .getPrototypes(query)
                      .then((res: AxiosResponse) => {
                        return res?.data;
                      })
                      .catch(handleHttpError);
                  }}
                  onChange={(e, value): void => setFieldValue("prototype", value)}
                  mandatory
                  map={(value): { key: string; label: string } => ({
                    key: `${value.id}`,
                    label: `${value.name} (${
                      value.responsibleLaboratory.name ? value.responsibleLaboratory.name : ""
                    })`,
                  })}
                  value={values.prototype}
                  error={!!(errors.prototype && touched.prototype)}
                />
              </Grid>
            </Grid>
            <Grid container item direction="row" justify="flex-start">
              <span onClick={handleNavigation} className="helperTextLink" role="link" tabIndex={0}>
                Not found? Create new tesa Prototype
              </span>
            </Grid>
            {!hideSubmit && (
              <Grid container item direction="row" justify="flex-end">
                <CustomButton className={classes.marginTop} disabled={isSubmitting} primary text="Use" type="submit" />
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default PrototypeSampleAddForm;
